import React, { useRef, useEffect, useState } from 'react';
import { Animated, Pressable, StyleSheet, AccessibilityInfo } from 'react-native';

import AppStyles from '../AppStyles';

/**
 * ToggleSwitch - iOS-style toggle switch for V3 design system
 *
 * @param {boolean} value - Current toggle state
 * @param {function} onValueChange - Callback when toggle changes
 * @param {boolean} disabled - Whether the toggle is disabled
 * @param {boolean} small - Use smaller size for compact layouts
 * @param {object} style - Additional styles for the container
 */
const ToggleSwitch = ({ value, onValueChange, disabled = false, small = false, style }) => {
  const translateDistance = small ? 14 : 20;
  const translateX = useRef(new Animated.Value(value ? translateDistance : 0)).current;
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  // Listen for reduced motion preference (WCAG AAA)
  useEffect(() => {
    AccessibilityInfo.isReduceMotionEnabled().then(setPrefersReducedMotion);
    const subscription = AccessibilityInfo.addEventListener(
      'reduceMotionChanged',
      setPrefersReducedMotion
    );
    return () => subscription.remove();
  }, []);

  useEffect(() => {
    if (prefersReducedMotion) {
      // Instant transition when reduced motion is preferred
      translateX.setValue(value ? translateDistance : 0);
    } else {
      Animated.spring(translateX, {
        toValue: value ? translateDistance : 0,
        useNativeDriver: true,
        friction: 8,
        tension: 100,
      }).start();
    }
  }, [value, translateX, prefersReducedMotion, translateDistance]);

  const handlePress = () => {
    if (!disabled && onValueChange) {
      onValueChange(!value);
    }
  };

  const trackColor = value
    ? AppStyles.colorSet.toggleTrackOn
    : AppStyles.colorSet.toggleTrackOff;

  return (
    <Pressable
      onPress={handlePress}
      disabled={disabled}
      style={[
        styles.container,
        small && styles.containerSmall,
        !disabled && { cursor: 'pointer' },
        style,
        disabled && styles.disabled,
      ]}
      accessibilityRole="switch"
      accessibilityState={{ checked: value, disabled }}
    >
      {({ hovered, focused }) => (
        <Animated.View
          style={[
            styles.track,
            small && styles.trackSmall,
            { backgroundColor: trackColor },
            !disabled && (hovered || focused) && styles.trackHovered,
            disabled && styles.trackDisabled,
          ]}
        >
          <Animated.View
            style={[
              styles.thumb,
              small && styles.thumbSmall,
              { transform: [{ translateX }] },
              disabled && styles.thumbDisabled,
            ]}
          />
        </Animated.View>
      )}
    </Pressable>
  );
};

const styles = StyleSheet.create({
  container: {
    justifyContent: 'center',
    // WCAG minimum touch target: 44x44px
    minHeight: 44,
    minWidth: 51,
  },
  containerSmall: {
    minHeight: 28,
    minWidth: 36,
  },
  track: {
    width: 51,
    height: 31,
    borderRadius: 16,
    justifyContent: 'center',
    paddingHorizontal: 2,
  },
  trackSmall: {
    width: 36,
    height: 22,
    borderRadius: 11,
  },
  trackDisabled: {
    opacity: 0.5,
  },
  // Web hover/focus feedback — soft gold glow around the track
  trackHovered: {
    shadowColor: AppStyles.colorSet.brandPrimary,
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.55,
    shadowRadius: 4,
    elevation: 0,
  },
  thumb: {
    width: 27,
    height: 27,
    borderRadius: 14,
    backgroundColor: AppStyles.colorSet.toggleThumb,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
    elevation: 3,
  },
  thumbSmall: {
    width: 18,
    height: 18,
    borderRadius: 9,
  },
  thumbDisabled: {
    backgroundColor: '#E5E5E5',
  },
  disabled: {
    opacity: 0.6,
  },
});

export default ToggleSwitch;
